import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Scale, Upload, Shield, FileText, CheckCircle2, AlertTriangle, Loader2,
  ChevronRight, Eye, X, PlusCircle, Trash2, ArrowLeft, Info, Search, Calculator, Download, TrendingUp
} from "lucide-react";
import { api, API } from "@/lib/api";
import { usePaywall } from "@/hooks/usePaywall";

const brl = (cents = 0) =>
  `R$ ${((cents || 0) / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CONF_TONE = (c) => c >= 0.85 ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                    : c >= 0.65 ? "text-amber-700 bg-amber-50 border-amber-200"
                    : "text-rose-700 bg-rose-50 border-rose-200";
const CONF_LABEL = (c) => c >= 0.85 ? "ALTA" : c >= 0.65 ? "MÉDIA" : "BAIXA";

// ----------- List / Landing -----------
function SentenceUpdatesList({ onOpen, onNew }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/sentence-updates").then(r => setRows(r.data.rows || [])).finally(() => setLoading(false));
  }, []);
  return (
    <div className="flex-1 min-w-0 flex flex-col p-6 lg:p-8 font-sans max-w-6xl mx-auto w-full space-y-6" data-testid="su-list">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#a37417]">Cálculo Judicial</div>
          <h1 className="font-heading text-3xl font-semibold text-slate-900 flex items-center gap-3">
            <Scale className="w-7 h-7 text-[#a37417]"/> Atualização de Sentença
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Motor jurídico-financeiro para reconstrução de título executivo. Leia sentença e acórdão
            com IA, valide os parâmetros com trechos-origem, e (em breve) gere memória de cálculo
            profissional consultando índices oficiais.
          </p>
        </div>
        <button onClick={onNew}
          className="h-11 px-5 rounded-lg bg-[#d4a24c] hover:bg-[#c48a3e] text-black font-semibold inline-flex items-center gap-2"
          data-testid="su-new-btn">
          <PlusCircle className="w-4 h-4"/> Nova atualização
        </button>
      </div>

      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 flex items-start gap-3">
        <Info className="w-5 h-5 shrink-0 mt-0.5"/>
        <div>
          <strong>Fase 2 ativa</strong> — Nesta fase o Hermes lê a sentença/acórdão via OCR (GPT + Gemini),
          extrai os parâmetros com matriz de confiança e trechos-origem auditáveis. O motor financeiro
          determinístico + consulta Banco Central (Fase 1) e a consolidação sentença × acórdão (Fase 3)
          estão na fila.
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100">
          <h2 className="font-heading text-lg font-semibold text-slate-900">Meus cálculos</h2>
        </div>
        {loading ? (
          <div className="p-8 text-center text-slate-500 text-sm">Carregando...</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm" data-testid="su-empty">
            Nenhum cálculo ainda. Clique em "Nova atualização" para começar.
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {rows.map(r => {
              const badge = {
                DRAFT: {t:"Rascunho", c:"bg-slate-100 text-slate-700"},
                UPLOADED: {t:"Aguardando autorização", c:"bg-blue-100 text-blue-700"},
                AUTHORIZED: {t:"Pronto para analisar", c:"bg-indigo-100 text-indigo-700"},
                ANALYZING: {t:"Analisando", c:"bg-amber-100 text-amber-800"},
                REVIEW: {t:"Aguardando conferência", c:"bg-emerald-100 text-emerald-800"},
                ERROR: {t:"Erro", c:"bg-rose-100 text-rose-700"},
              }[r.status] || {t:r.status, c:"bg-slate-100"};
              return (
                <button key={r.id} onClick={()=>onOpen(r.id)}
                  className="w-full text-left px-6 py-4 hover:bg-slate-50 flex items-center gap-4"
                  data-testid={`su-row-${r.id}`}>
                  <div className="w-10 h-10 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center">
                    <Scale className="w-5 h-5 text-[#a37417]"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{r.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5 truncate">
                      {r.case_number || "—"} · {r.creditor || "—"} × {r.debtor || "—"}
                      {" · "}{r.regime === "justica_comum" ? "Justiça Comum" : "Juizado Especial"}
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-[11px] font-medium ${badge.c}`}>{badge.t}</span>
                  <ChevronRight className="w-4 h-4 text-slate-300"/>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ----------- New Wizard -----------
function NewWizard({ onCreated, onCancel }) {
  const [regime, setRegime] = useState("");
  const [meta, setMeta] = useState({ title:"", case_number:"", creditor:"", debtor:"" });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!regime) return toast.error("Escolha o regime processual");
    setSaving(true);
    try {
      const r = await api.post("/sentence-updates", { regime, ...meta });
      toast.success("Cálculo criado — envie a sentença");
      onCreated(r.data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao criar");
    } finally { setSaving(false); }
  };

  const Field = ({ label, value, onChange, testid, placeholder }) => (
    <label className="block">
      <div className="text-xs font-medium text-slate-600 mb-1">{label}</div>
      <input value={value} onChange={onChange} placeholder={placeholder} data-testid={testid}
        className="w-full h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 focus:ring-2 focus:ring-[#d4a24c] outline-none text-sm"/>
    </label>
  );

  return (
    <div className="p-8 max-w-3xl mx-auto space-y-6" data-testid="su-wizard-new">
      <button onClick={onCancel} className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
        <ArrowLeft className="w-4 h-4"/> Voltar
      </button>
      <div>
        <div className="text-[11px] uppercase tracking-widest text-[#a37417]">Etapa 1 · Tipo de Processo</div>
        <h2 className="font-heading text-2xl font-semibold text-slate-900">Qual o regime processual?</h2>
        <p className="text-sm text-slate-600 mt-1">O art. 523 do CPC tem tratamento diferente em cada regime — por isso é o primeiro passo.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button onClick={()=>setRegime("justica_comum")} data-testid="su-regime-comum"
          className={`text-left rounded-2xl border-2 p-6 transition ${regime==="justica_comum" ? "border-[#d4a24c] bg-amber-50 shadow-md" : "border-slate-200 hover:border-slate-300"}`}>
          <Scale className={`w-6 h-6 ${regime==="justica_comum" ? "text-[#a37417]" : "text-slate-500"}`}/>
          <div className="mt-2 font-heading font-semibold text-slate-900">Justiça Comum</div>
          <div className="text-xs text-slate-500 mt-1">
            Aplica art. 523: multa de 10% + honorários adicionais de 10% se inadimplemento.
          </div>
        </button>
        <button onClick={()=>setRegime("juizado_especial")} data-testid="su-regime-jec"
          className={`text-left rounded-2xl border-2 p-6 transition ${regime==="juizado_especial" ? "border-[#d4a24c] bg-amber-50 shadow-md" : "border-slate-200 hover:border-slate-300"}`}>
          <Scale className={`w-6 h-6 ${regime==="juizado_especial" ? "text-[#a37417]" : "text-slate-500"}`}/>
          <div className="mt-2 font-heading font-semibold text-slate-900">Juizado Especial Cível</div>
          <div className="text-xs text-slate-500 mt-1">
            Multa de 10% quando cabível; honorários do art. 523 não aplicam automaticamente (Enunciado 97 FONAJE).
          </div>
        </button>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
        <div className="text-[11px] uppercase tracking-widest text-slate-500">Identificação (opcional agora, pode ser completado após a leitura)</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Título do cálculo" value={meta.title} onChange={e=>setMeta({...meta, title:e.target.value})} testid="su-meta-title" placeholder="Ex.: Atualização Silva × Empresa X"/>
          <Field label="Nº do processo" value={meta.case_number} onChange={e=>setMeta({...meta, case_number:e.target.value})} testid="su-meta-case" placeholder="0000000-00.0000.0.00.0000"/>
          <Field label="Exequente / Autor" value={meta.creditor} onChange={e=>setMeta({...meta, creditor:e.target.value})} testid="su-meta-creditor"/>
          <Field label="Executado / Réu" value={meta.debtor} onChange={e=>setMeta({...meta, debtor:e.target.value})} testid="su-meta-debtor"/>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <button onClick={onCancel} className="h-11 px-5 rounded-lg border border-slate-200 text-slate-700 text-sm">Cancelar</button>
        <button onClick={submit} disabled={saving || !regime}
          className="h-11 px-6 rounded-lg bg-[#d4a24c] hover:bg-[#c48a3e] disabled:opacity-40 text-black font-semibold inline-flex items-center gap-2"
          data-testid="su-wizard-continue">
          {saving ? <><Loader2 className="w-4 h-4 animate-spin"/> Criando...</> : <>Continuar <ChevronRight className="w-4 h-4"/></>}
        </button>
      </div>
    </div>
  );
}

function CalculationPanel({ doc, reload }) {
  const verbas = doc.extracted?.verbas || [];
  const lastCalcId = doc.last_calculation_id;
  const calculations = doc.calculations || [];
  const lastCalc = calculations.find(c => c.id === lastCalcId) || calculations[calculations.length - 1];

  const [verbaIdx, setVerbaIdx] = useState(0);
  const [dataFinal, setDataFinal] = useState(new Date().toISOString().slice(0,10));
  const [multa, setMulta] = useState(0);
  const [hono, setHono] = useState(0);
  const [honoBase, setHonoBase] = useState("atualizado");
  const [art523, setArt523] = useState(false);
  const [busy, setBusy] = useState(false);
  const [missing, setMissing] = useState(null);
  const { authorize, PaywallElement } = usePaywall("CALCULO_UPDATE");

  const brl = (v) => `R$ ${Number(v).toLocaleString("pt-BR", {minimumFractionDigits:2, maximumFractionDigits:2})}`;

  const doCalculate = async (extraOverrides = {}) => {
    // Só cobra no PRIMEIRO cálculo — resumeWithOverrides após MISSING_PARAMS não recobra
    const isResume = Object.keys(extraOverrides).length > 0;
    let authorization = null;
    if (!isResume) {
      authorization = await authorize({ quantity: 1 });
      if (!authorization) { toast.info("Cálculo cancelado."); return; }
    }
    setBusy(true);
    try {
      const r = await api.post(`/sentence-updates/${doc.id}/calculate`, {
        verba_index: verbaIdx,
        data_final: dataFinal,
        multa_percentual: multa,
        honorarios_percentual: hono,
        honorarios_base: honoBase,
        apply_art_523: art523,
        overrides: extraOverrides,
      });
      if (r.data.status === "MISSING_PARAMS") {
        setMissing({
          params: r.data.missing_params || [],
          current_params: r.data.current_params || {},
          existing_overrides: extraOverrides,
        });
        // NÃO commita — usuário vai preencher e chamar resumeWithOverrides
        // que consumirá a mesma reserva (backend idempotency)
        return;
      }
      if (authorization) await authorization.commit();
      toast.success("Cálculo concluído");
      setMissing(null);
      await reload();
    } catch (e) {
      if (authorization) await authorization.release();
      toast.error(e?.response?.data?.detail || "Falha no cálculo");
    } finally { setBusy(false); }
  };

  const run = () => doCalculate();
  const resumeWithOverrides = (fills) => doCalculate({ ...(missing?.existing_overrides || {}), ...fills });

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="su-calc-panel">
      <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
        <Calculator className="w-5 h-5 text-[#a37417]"/>
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500">Fase 1 · Motor financeiro determinístico</div>
          <h2 className="font-heading text-lg font-semibold text-slate-900">Calcular verba</h2>
        </div>
      </div>
      <div className="p-6 grid grid-cols-1 md:grid-cols-4 gap-3">
        <label className="block md:col-span-2">
          <div className="text-xs text-slate-600 mb-1">Verba</div>
          <select value={verbaIdx} onChange={e=>setVerbaIdx(Number(e.target.value))}
            className="w-full h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 text-sm"
            data-testid="su-calc-verba-select">
            {verbas.map((v, i) => {
              const label = `${(v.tipo || "outros").replace("_"," ")} — ${v.descricao || ""} — R$ ${((v.principal_valor_cents||0)/100).toLocaleString("pt-BR",{minimumFractionDigits:2})}`;
              return <option key={i} value={i}>{label}</option>;
            })}
          </select>
        </label>
        <label className="block">
          <div className="text-xs text-slate-600 mb-1">Atualizar até</div>
          <input type="date" value={dataFinal} onChange={e=>setDataFinal(e.target.value)}
            className="w-full h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 text-sm"
            data-testid="su-calc-datafinal"/>
        </label>
        <div/>
        <label className="block">
          <div className="text-xs text-slate-600 mb-1">Multa % (art. 523)</div>
          <input type="number" step="0.01" value={multa} onChange={e=>setMulta(Number(e.target.value))}
            className="w-full h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 text-sm"
            data-testid="su-calc-multa"/>
        </label>
        <label className="block">
          <div className="text-xs text-slate-600 mb-1">Honorários %</div>
          <input type="number" step="0.01" value={hono} onChange={e=>setHono(Number(e.target.value))}
            className="w-full h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 text-sm"
            data-testid="su-calc-hono"/>
        </label>
        <label className="block">
          <div className="text-xs text-slate-600 mb-1">Base honorários</div>
          <select value={honoBase} onChange={e=>setHonoBase(e.target.value)}
            className="w-full h-10 px-3 rounded-lg bg-slate-50 border border-slate-200 text-sm">
            <option value="atualizado">Valor atualizado</option>
            <option value="principal">Principal</option>
          </select>
        </label>
        <div className="flex items-end">
          <button onClick={run} disabled={busy || verbas.length === 0}
            className="w-full h-10 rounded-lg bg-[#d4a24c] hover:bg-[#c48a3e] disabled:opacity-40 text-black font-semibold text-sm inline-flex items-center justify-center gap-2"
            data-testid="su-calc-run">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin"/> Calculando...</> : <><Calculator className="w-4 h-4"/> Calcular</>}
          </button>
        </div>
      </div>
      <div className="px-6 pb-4">
        <label className="inline-flex items-center gap-2 text-xs text-slate-700 cursor-pointer" data-testid="su-calc-art523">
          <input type="checkbox" checked={art523} onChange={e=>setArt523(e.target.checked)}
            className="rounded border-slate-300 text-[#d4a24c] focus:ring-[#d4a24c]"/>
          <span>Aplicar art. 523 CPC automaticamente (multa 10% + honorários 10% na Justiça Comum; apenas multa no JEC)</span>
        </label>
      </div>
      {lastCalc && <CalcResult doc={doc} entry={lastCalc}/>}
      {missing && (
        <MissingParamsModal
          missing={missing}
          onCancel={()=>setMissing(null)}
          onSubmit={resumeWithOverrides}
          busy={busy}
        />
      )}
      {PaywallElement}
    </div>
  );
}

function MissingParamsModal({ missing, onCancel, onSubmit, busy }) {
  const [values, setValues] = useState({});
  const params = missing.params || [];
  const canSubmit = params.every(p => (values[p.field] || "").toString().trim());
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white w-full max-w-2xl max-h-[85vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col" onClick={e=>e.stopPropagation()} data-testid="su-missing-modal">
        <div className="px-6 py-4 border-b border-amber-200 bg-amber-50">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-700 shrink-0 mt-0.5"/>
            <div>
              <div className="text-[11px] uppercase tracking-widest text-amber-700 font-semibold">Informações necessárias</div>
              <h3 className="font-heading text-xl font-semibold text-slate-900">Parâmetro(s) ausente(s) na sentença</h3>
              <p className="text-sm text-slate-700 mt-1">
                Para prosseguir com o cálculo, precisamos que você complete {params.length} informação(ões) que não conseguimos localizar com segurança no texto da decisão.
              </p>
            </div>
          </div>
        </div>
        <div className="p-6 overflow-y-auto flex-1 space-y-5">
          {params.map((p) => (
            <div key={p.field} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`su-missing-${p.field}`}>
              <div className="flex items-start gap-2 mb-1">
                <div className="text-sm font-semibold text-slate-900">{p.label}</div>
              </div>
              <div className="text-sm text-rose-700 mb-1"><strong>Motivo:</strong> {p.reason}</div>
              {p.hint && <div className="text-xs text-slate-600 mb-2">{p.hint}</div>}
              {p.snippet && (
                <div className="text-xs italic text-slate-700 bg-slate-50 border border-slate-200 rounded p-2 mb-3">
                  "{p.snippet}"
                </div>
              )}
              {p.kind === "date" ? (
                <input
                  type="date"
                  value={values[p.field] || ""}
                  onChange={e=>setValues(v => ({...v, [p.field]: e.target.value}))}
                  className="w-full h-11 px-3 rounded-lg bg-slate-50 border-2 border-amber-300 focus:border-[#d4a24c] outline-none text-sm"
                  data-testid={`su-missing-input-${p.field}`}
                />
              ) : p.kind === "select" ? (
                <select
                  value={values[p.field] || ""}
                  onChange={e=>setValues(v => ({...v, [p.field]: e.target.value}))}
                  className="w-full h-11 px-3 rounded-lg bg-slate-50 border-2 border-amber-300 focus:border-[#d4a24c] outline-none text-sm"
                  data-testid={`su-missing-input-${p.field}`}
                >
                  <option value="">— Selecione —</option>
                  {(p.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input
                  type="text"
                  placeholder={p.example || ""}
                  value={values[p.field] || ""}
                  onChange={e=>setValues(v => ({...v, [p.field]: e.target.value}))}
                  className="w-full h-11 px-3 rounded-lg bg-slate-50 border-2 border-amber-300 focus:border-[#d4a24c] outline-none text-sm"
                  data-testid={`su-missing-input-${p.field}`}
                />
              )}
              {p.example && <div className="text-[10px] text-slate-400 mt-1">Formato esperado: {p.example}</div>}
            </div>
          ))}
        </div>
        <div className="px-6 py-3 border-t border-slate-100 flex justify-between items-center bg-slate-50">
          <button onClick={onCancel} className="text-sm text-slate-600 hover:text-slate-900">Cancelar</button>
          <button onClick={()=>onSubmit(values)} disabled={!canSubmit || busy}
            className="h-11 px-5 rounded-lg bg-[#d4a24c] hover:bg-[#c48a3e] disabled:opacity-40 text-black font-semibold text-sm inline-flex items-center gap-2"
            data-testid="su-missing-submit">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin"/> Recalculando...</> : <>Continuar cálculo com essas informações</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function CalcResult({ doc, entry }) {
  const brl = (v) => `R$ ${Number(v).toLocaleString("pt-BR", {minimumFractionDigits:2, maximumFractionDigits:2})}`;
  const t = entry.result.totals;
  const memory = entry.result.memory || [];
  const warnings = entry.warnings || [];
  return (
    <div className="border-t border-slate-100 bg-slate-50 p-6" data-testid="su-calc-result">
      {warnings.length > 0 && (
        <div className="mb-4 space-y-2" data-testid="su-calc-warnings">
          {warnings.map((w, i) => (
            <div key={i} className={`rounded-xl p-3 text-sm flex items-start gap-2 ${entry.is_fazenda_publica ? "bg-rose-50 border-2 border-rose-300 text-rose-900" : "bg-amber-50 border border-amber-300 text-amber-900"}`}>
              <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5"/>
              <div>{w}</div>
            </div>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
        {[
          { label: "Principal", v: t.principal },
          { label: "Correção", v: t.correcao_monetaria },
          { label: "Juros", v: t.juros },
          { label: "Multa", v: t.multa },
          { label: "Honorários", v: t.honorarios },
          { label: "TOTAL", v: t.total_final, hi: true },
        ].map((c, i) => (
          <div key={i} className={`rounded-xl p-3 border ${c.hi ? "bg-[#d4a24c] border-[#c48a3e] text-black" : "bg-white border-slate-200"}`}>
            <div className={`text-[10px] uppercase tracking-widest ${c.hi ? "text-black/70" : "text-slate-500"}`}>{c.label}</div>
            <div className={`text-lg font-semibold mt-1 ${c.hi ? "text-black" : "text-slate-900"}`}>{brl(c.v)}</div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp className="w-4 h-4 text-slate-500"/>
        <div className="text-sm font-medium text-slate-700">Memória mês a mês ({memory.length} linhas)</div>
        <div className="flex-1"/>
        <a href={`${API}/sentence-updates/${doc.id}/calculations/${entry.id}/pdf`} target="_blank" rel="noopener"
          className="h-9 px-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold inline-flex items-center gap-2"
          data-testid="su-calc-pdf-btn">
          <Download className="w-3.5 h-3.5"/> Baixar PDF
        </a>
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white max-h-96 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-900 text-white sticky top-0 z-10 shadow-sm">
            <tr>
              <th className="text-left px-3 py-2">Competência</th>
              <th className="text-right px-3 py-2">Principal</th>
              <th className="text-left px-3 py-2">Índice</th>
              <th className="text-right px-3 py-2">Fator</th>
              <th className="text-right px-3 py-2">Correção</th>
              <th className="text-right px-3 py-2">Juros</th>
              <th className="text-right px-3 py-2">Saldo</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {memory.map((r, i) => (
              <tr key={i} className={i % 2 ? "bg-slate-50" : ""}>
                <td className="px-3 py-1.5 font-mono">{r.competencia}</td>
                <td className="px-3 py-1.5 text-right">{brl(r.principal_periodo)}</td>
                <td className="px-3 py-1.5">{r.indice || "—"}</td>
                <td className="px-3 py-1.5 text-right font-mono text-[10px]">{r.fator_indice}</td>
                <td className="px-3 py-1.5 text-right">{brl(r.correcao)}</td>
                <td className="px-3 py-1.5 text-right">{brl(r.juros)}</td>
                <td className="px-3 py-1.5 text-right font-semibold">{brl(r.saldo_atualizado)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-2 text-[10px] text-slate-500">
        Fontes oficiais: {Object.entries(entry.result.sources || {}).map(([k, m]) => `${k} (${m.source})`).join(" · ") || "—"} · Motor v{entry.version}
      </div>
    </div>
  );
}

// ----------- Detail (upload + authorize + analyze + parameters) -----------
function Detail({ id, onBack }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showAuth, setShowAuth] = useState(null); // 'sentence' | 'appeal' | null
  const [origin, setOrigin] = useState(null); // { doc, page, snippet, label }

  const load = () => {
    setLoading(true);
    api.get(`/sentence-updates/${id}`).then(r => setDoc(r.data)).finally(()=>setLoading(false));
  };
  useEffect(load, [id]);

  const upload = async (role, file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) return toast.error("Envie um PDF");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("role", role);
    try {
      await api.post(`/sentence-updates/${id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" }});
      toast.success(role === "sentence" ? "Sentença enviada" : "Acórdão enviado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no upload");
    } finally { setBusy(false); }
  };

  const authorize = async (which) => {
    setBusy(true);
    try {
      await api.post(`/sentence-updates/${id}/authorize`, { [which]: true });
      toast.success("Autorização registrada");
      setShowAuth(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao autorizar");
    } finally { setBusy(false); }
  };

  const analyze = async () => {
    setBusy(true);
    try {
      toast.info("A IA está analisando a sentença — pode levar até 60s...", { duration: 3000 });
      await api.post(`/sentence-updates/${id}/analyze`);
      toast.success("Parâmetros extraídos");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha na análise");
    } finally { setBusy(false); }
  };

  if (loading || !doc) return <div className="p-10 text-slate-500 text-sm">Carregando cálculo...</div>;

  const sen = doc.docs?.sentence;
  const app_ = doc.docs?.appeal;
  const extracted = doc.extracted;
  const canAnalyze = sen && sen.authorized;

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-6" data-testid="su-detail">
      <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
        <ArrowLeft className="w-4 h-4"/> Voltar
      </button>

      <div>
        <div className="text-[11px] uppercase tracking-widest text-[#a37417]">
          {doc.regime === "justica_comum" ? "Justiça Comum" : "Juizado Especial Cível"}
        </div>
        <h1 className="font-heading text-3xl font-semibold text-slate-900">{doc.title}</h1>
        <div className="text-sm text-slate-600 mt-1">
          {doc.case_number || "—"} · {doc.creditor || "—"} × {doc.debtor || "—"}
        </div>
      </div>

      {/* Upload cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <UploadCard
          role="sentence" label="Sentença" required
          info={sen} onFile={(f)=>upload("sentence", f)}
          onAuthorize={()=>setShowAuth("sentence")}
          busy={busy}
        />
        <UploadCard
          role="appeal" label="Acórdão" toggleHint="Houve acórdão?"
          info={app_} onFile={(f)=>upload("appeal", f)}
          onAuthorize={()=>setShowAuth("appeal")}
          busy={busy}
        />
      </div>

      {/* Analyze */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500">Etapa 3 · Análise IA</div>
            <h2 className="font-heading text-lg font-semibold text-slate-900">Extrair parâmetros</h2>
            <p className="text-xs text-slate-500 mt-1">
              A IA lê integralmente sentença {app_ ? "e acórdão" : ""} e devolve os parâmetros com matriz de confiança e trechos-origem.
            </p>
          </div>
          <button
            onClick={analyze}
            disabled={!canAnalyze || busy || doc.status === "ANALYZING"}
            className="h-11 px-6 rounded-lg bg-slate-900 hover:bg-slate-800 disabled:opacity-40 text-white font-semibold inline-flex items-center gap-2"
            data-testid="su-analyze-btn"
          >
            {busy || doc.status === "ANALYZING" ? <><Loader2 className="w-4 h-4 animate-spin"/> Analisando...</> : <>Analisar título</>}
          </button>
        </div>
        {!canAnalyze && (
          <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
            Envie e <strong>autorize</strong> a leitura da sentença para liberar a análise.
          </div>
        )}
      </div>

      {/* Extracted parameters */}
      {extracted && !extracted.parse_error && (
        <ExtractedPanel data={extracted} onViewOrigin={setOrigin} />
      )}
      {extracted && extracted.parse_error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-800">
          <div className="font-medium">A IA respondeu, mas o JSON não foi interpretado.</div>
          <pre className="mt-2 text-[11px] whitespace-pre-wrap max-h-48 overflow-auto bg-white p-2 rounded border border-rose-200">{extracted.raw?.slice(0, 500)}</pre>
          <div className="mt-2">Tente <button onClick={analyze} className="underline">reanalisar</button>.</div>
        </div>
      )}

      {/* Fase 1 — Motor Financeiro */}
      {extracted && !extracted.parse_error && extracted.verbas && (
        <CalculationPanel doc={doc} reload={load}/>
      )}

      {showAuth && (
        <AuthorizeModal
          which={showAuth}
          onConfirm={()=>authorize(showAuth)}
          onCancel={()=>setShowAuth(null)}
          busy={busy}
        />
      )}

      {origin && (
        <OriginModal data={origin} onClose={()=>setOrigin(null)} />
      )}
    </div>
  );
}

function UploadCard({ role, label, info, onFile, onAuthorize, busy, toggleHint, required }) {
  const [fileInput, setFileInput] = useState(null);
  const uploaded = !!info;
  const authorized = info?.authorized;
  return (
    <div className={`rounded-2xl border-2 ${uploaded ? "border-slate-200" : "border-dashed border-slate-300"} bg-white p-6 shadow-sm`} data-testid={`su-upload-${role}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500">
            {role === "sentence" ? "Etapa 2 · Documento principal" : "Etapa 2b · Documento opcional"}
          </div>
          <h3 className="font-heading text-lg font-semibold text-slate-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#a37417]"/> {label}
            {required && <span className="text-rose-600 text-sm">*</span>}
          </h3>
          {toggleHint && !uploaded && <p className="text-xs text-slate-500 mt-1">{toggleHint}</p>}
        </div>
        {authorized && <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 px-2 py-1 rounded-full text-[11px] font-medium border border-emerald-200"><Shield className="w-3 h-3"/> autorizado</span>}
      </div>

      {!uploaded ? (
        <label className="mt-4 block cursor-pointer">
          <input type="file" accept="application/pdf" className="hidden"
            ref={setFileInput}
            onChange={(e)=>onFile(e.target.files?.[0])}
            data-testid={`su-upload-input-${role}`}/>
          <div className="border border-slate-200 rounded-xl px-4 py-6 flex flex-col items-center gap-2 text-slate-600 hover:bg-slate-50">
            <Upload className="w-6 h-6"/>
            <div className="text-sm">Arraste ou clique — <strong>PDF</strong> (nativo ou digitalizado)</div>
            <div className="text-[11px] text-slate-400">Todas as páginas serão lidas</div>
          </div>
        </label>
      ) : (
        <div className="mt-4 space-y-3">
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 flex items-center justify-between">
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-900 truncate">{info.filename}</div>
              <div className="text-[11px] text-slate-500">{Math.round((info.size_bytes || 0)/1024)} KB · enviado {new Date(info.uploaded_at).toLocaleString("pt-BR")}</div>
            </div>
            <FileText className="w-4 h-4 text-slate-400"/>
          </div>
          {!authorized ? (
            <button onClick={onAuthorize} disabled={busy}
              className="w-full h-10 rounded-lg bg-[#0b0f1a] hover:bg-slate-900 text-[#d4a24c] text-sm font-semibold inline-flex items-center justify-center gap-2 border border-[#3a2a15]"
              data-testid={`su-authorize-${role}`}>
              <Shield className="w-4 h-4"/> Autorizar leitura pela IA
            </button>
          ) : (
            <div className="text-xs text-emerald-700 flex items-center gap-1">
              <CheckCircle2 className="w-4 h-4"/> Pronto para analisar
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AuthorizeModal({ which, onConfirm, onCancel, busy }) {
  const label = which === "sentence" ? "sentença" : "acórdão";
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl p-6" onClick={e=>e.stopPropagation()} data-testid="su-authorize-modal">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center">
            <Shield className="w-5 h-5 text-[#a37417]"/>
          </div>
          <div>
            <h3 className="font-heading text-lg font-semibold text-slate-900">Autorizar leitura da {label}?</h3>
            <p className="text-sm text-slate-600 mt-1">
              A IA fará leitura integral do documento (todas as páginas, com OCR se necessário) para
              identificar os parâmetros do cálculo. Nenhum dado é modificado no documento original.
            </p>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onCancel} className="h-10 px-5 rounded-lg border border-slate-200 text-slate-700 text-sm">Cancelar</button>
          <button onClick={onConfirm} disabled={busy}
            className="h-10 px-5 rounded-lg bg-[#d4a24c] hover:bg-[#c48a3e] text-black text-sm font-semibold inline-flex items-center gap-2"
            data-testid="su-authorize-confirm">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin"/> Autorizando...</> : <>Autorizar leitura</>}
          </button>
        </div>
      </div>
    </div>
  );
}

function OriginModal({ data, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl" onClick={e=>e.stopPropagation()} data-testid="su-origin-modal">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500">Ver origem</div>
            <h3 className="font-heading text-lg font-semibold text-slate-900">{data.label || "Trecho de origem"}</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900"><X className="w-5 h-5"/></button>
        </div>
        <div className="p-6 space-y-3">
          <div className="text-xs text-slate-500">
            Documento: <strong className="text-slate-800">{data.doc === "acordao" ? "Acórdão" : "Sentença"}</strong> · Página: <strong className="text-slate-800">{data.page || "—"}</strong>
          </div>
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 text-sm text-slate-800 whitespace-pre-wrap italic">
            {data.snippet ? `"${data.snippet}"` : "(sem trecho literal disponível)"}
          </div>
          <p className="text-[11px] text-slate-500">A visualização da página em PDF chegará junto com a Fase 1.</p>
        </div>
      </div>
    </div>
  );
}

function ExtractedPanel({ data, onViewOrigin }) {
  const processo = data.processo || {};
  const verbas = data.verbas || [];
  const alteracoes = data.acordao_alteracoes || [];
  const alertas = data.alertas || [];

  const openOrigin = (source, label) => {
    if (!source) return;
    onViewOrigin({ ...source, label });
  };

  return (
    <div className="space-y-6" data-testid="su-extracted">
      {/* Alertas */}
      {alertas.length > 0 && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 space-y-2">
          {alertas.map((a, i) => (
            <div key={i} className="flex items-start gap-2 text-sm text-amber-900" data-testid={`su-alert-${i}`}>
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5"/>
              <div><strong>{a.titulo || "Aviso"}</strong> — {a.mensagem}</div>
            </div>
          ))}
        </div>
      )}

      {/* Processo */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100">
          <div className="text-[11px] uppercase tracking-widest text-slate-500">Identificação</div>
          <h2 className="font-heading text-lg font-semibold text-slate-900">Processo</h2>
        </div>
        <table className="w-full text-sm">
          <tbody className="divide-y divide-slate-100">
            {["numero","tribunal","comarca","vara","classe","regime","autor","reu"].map(k => {
              const f = processo[k];
              if (!f) return null;
              const conf = typeof f.confidence === "number" ? f.confidence : null;
              return (
                <tr key={k} data-testid={`su-processo-${k}`}>
                  <td className="px-6 py-2 text-xs uppercase tracking-wider text-slate-500 w-32">{k.replace("_", " ")}</td>
                  <td className="px-6 py-2 text-slate-900">{f.value || "—"}</td>
                  <td className="px-6 py-2 w-32">
                    {conf !== null && <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium border ${CONF_TONE(conf)}`}>{CONF_LABEL(conf)} · {Math.round(conf*100)}%</span>}
                  </td>
                  <td className="px-6 py-2 w-24 text-right">
                    {f.source && (
                      <button onClick={()=>openOrigin(f.source, `Processo · ${k}`)}
                        className="text-xs text-slate-500 hover:text-[#a37417] inline-flex items-center gap-1"
                        data-testid={`su-origin-${k}`}>
                        <Eye className="w-3 h-3"/> ver origem
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Verbas */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100">
          <div className="text-[11px] uppercase tracking-widest text-slate-500">Etapa 4 · Parâmetros identificados</div>
          <h2 className="font-heading text-lg font-semibold text-slate-900">Verbas condenatórias ({verbas.length})</h2>
        </div>
        {verbas.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">Nenhuma verba identificada.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {verbas.map((v, i) => (
              <VerbaCard key={v.id || i} v={v} onOrigin={openOrigin}/>
            ))}
          </div>
        )}
      </div>

      {/* Alterações do acórdão */}
      {alteracoes.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100">
            <div className="text-[11px] uppercase tracking-widest text-slate-500">Etapa 5 · Consolidação</div>
            <h2 className="font-heading text-lg font-semibold text-slate-900">Alterações do acórdão</h2>
            <p className="text-xs text-slate-500 mt-1">Uma matriz completa sentença × acórdão (com Título Executivo Consolidado) chega na Fase 3.</p>
          </div>
          <div className="divide-y divide-slate-100">
            {alteracoes.map((a, i) => (
              <div key={i} className="px-6 py-3 flex items-center gap-3 text-sm" data-testid={`su-alteracao-${i}`}>
                <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${
                  a.tipo === "REFORMADO" || a.tipo === "PARCIALMENTE_REFORMADO" ? "bg-rose-100 text-rose-700" :
                  a.tipo === "ANULADO" ? "bg-slate-200 text-slate-700" :
                  a.tipo === "ACRESCENTADO" ? "bg-indigo-100 text-indigo-700" :
                  "bg-emerald-100 text-emerald-800"
                }`}>{a.tipo}</span>
                <div className="flex-1 text-slate-800">{a.resumo}</div>
                {a.novo_valor_cents && <div className="text-sm font-medium text-slate-900">{brl(a.novo_valor_cents)}</div>}
                {a.sources?.[0] && (
                  <button onClick={()=>openOrigin(a.sources[0], `Acórdão · ${a.tipo}`)}
                    className="text-xs text-slate-500 hover:text-[#a37417] inline-flex items-center gap-1">
                    <Eye className="w-3 h-3"/> ver origem
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function VerbaCard({ v, onOrigin }) {
  const c = v.confidence || {};
  const rows = [
    { k: "Principal", value: v.principal_valor_cents ? brl(v.principal_valor_cents) : "—", conf: c.principal_valor },
    { k: "Data-base", value: v.principal_data_base || "—", conf: c.principal_data_base },
    { k: "Correção", value: v.correcao_indice || "—", conf: c.correcao_indice, extra: v.correcao_termo_inicial && `desde ${v.correcao_termo_inicial}` },
    { k: "Juros", value: v.juros_tipo || (v.juros_percentual ? `${v.juros_percentual}% ${v.juros_periodicidade || ""}` : "—"), conf: c.juros, extra: v.juros_termo_inicial && `desde ${v.juros_termo_inicial}` },
  ];
  return (
    <div className="px-6 py-4" data-testid={`su-verba-${v.id || v.tipo}`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-800 uppercase tracking-wider">{(v.tipo || "outros").replace("_", " ")}</span>
          <div className="mt-1 font-medium text-slate-900">{v.descricao || v.tipo}</div>
        </div>
        {v.sources?.[0] && (
          <button onClick={()=>onOrigin(v.sources[0], `Verba · ${v.descricao || v.tipo}`)}
            className="text-xs text-slate-500 hover:text-[#a37417] inline-flex items-center gap-1">
            <Eye className="w-3 h-3"/> ver origem
          </button>
        )}
      </div>
      <table className="w-full text-sm">
        <tbody className="divide-y divide-slate-100">
          {rows.map((r, i) => (
            <tr key={i}>
              <td className="py-1.5 text-xs uppercase tracking-wider text-slate-500 w-32">{r.k}</td>
              <td className="py-1.5 text-slate-900">
                {r.value}
                {r.extra && <span className="ml-2 text-xs text-slate-500">· {r.extra}</span>}
              </td>
              <td className="py-1.5 w-32">
                {typeof r.conf === "number" && <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium border ${CONF_TONE(r.conf)}`}>{CONF_LABEL(r.conf)} · {Math.round(r.conf*100)}%</span>}
              </td>
            </tr>
          ))}
          {v.honorarios_percentual && (
            <tr>
              <td className="py-1.5 text-xs uppercase tracking-wider text-slate-500">Honorários</td>
              <td className="py-1.5 text-slate-900">{v.honorarios_percentual}% {v.honorarios_base && <span className="text-xs text-slate-500">sobre {v.honorarios_base}</span>}</td>
              <td></td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ----------- Root component -----------
export default function SentenceUpdates() {
  const [view, setView] = useState("list"); // list | new | detail
  const [detailId, setDetailId] = useState(null);
  return (
    <>
      {view === "list" && (
        <SentenceUpdatesList
          onOpen={(id)=>{ setDetailId(id); setView("detail"); }}
          onNew={()=>setView("new")}
        />
      )}
      {view === "new" && (
        <NewWizard
          onCreated={(id)=>{ setDetailId(id); setView("detail"); }}
          onCancel={()=>setView("list")}
        />
      )}
      {view === "detail" && detailId && (
        <Detail id={detailId} onBack={()=>setView("list")}/>
      )}
    </>
  );
}
